import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Newspaper, Search, Tag, Clock, User, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchAllPosts, getAllTags, urlFor } from '../lib/sanity';

/**
 * Blog – list page showing all published articles with filtering
 */
export default function Blog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeTag, setActiveTag] = useState(searchParams.get('tag') || '');

  const allTags = useMemo(() => getAllTags(), []);

  // ── Fetch posts ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAllPosts({ tag: activeTag || undefined }).then((data) => {
      if (!cancelled) {
        setPosts(data || []);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeTag]);

  // ── Filter by search query ───────────────────────────────
  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return posts;
    const q = searchQuery.toLowerCase();
    return posts.filter(
      (p) =>
        p.title?.toLowerCase().includes(q) ||
        p.excerpt?.toLowerCase().includes(q) ||
        p.tags?.some((t) => t.toLowerCase().includes(q))
    );
  }, [posts, searchQuery]);

  // ── Tag click handler ────────────────────────────────────
  const handleTagClick = (tag) => {
    const next = tag === activeTag ? '' : tag;
    setActiveTag(next);
    const params = new URLSearchParams(searchParams);
    if (next) params.set('tag', next);
    else params.delete('tag');
    setSearchParams(params, { replace: true });
  };

  // ── Helpers ──────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const estimateReadTime = (post) => {
    const text = post.content
      ?.map((b) =>
        b.children?.map((c) => c.text || '').join(' ')
      )
      .join(' ');
    const words = text ? text.split(/\s+/).length : 200;
    return Math.max(1, Math.ceil(words / 200));
  };

  return (
    <div className="min-h-[calc(100vh-48px)] bg-gradient-to-b from-background to-muted/30">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-5xl mx-auto px-4 py-10 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Newspaper className="h-8 w-8" />
            <h1
              className="text-3xl md:text-4xl font-bold tracking-tight uppercase"
              style={{ fontFamily: 'Lato, Arial, sans-serif' }}
            >
              {t('blog.title') || 'Blog'}
            </h1>
          </div>
          <p className="text-white/70 max-w-xl mx-auto">
            {t('blog.description') || 'Strategy guides, card spotlights, and the latest news for the Ultraman Card Game.'}
          </p>

          {/* Search */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-6 max-w-md mx-auto relative"
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
            <Input
              placeholder={t('blog.searchPlaceholder') || 'Search articles…'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-white/30"
            />
          </form>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Tag filters */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <Tag className="h-4 w-4 text-muted-foreground mt-0.5" />
            {allTags.map((tag) => (
              <Badge
                key={tag}
                variant={tag === activeTag ? 'default' : 'outline'}
                className="cursor-pointer select-none"
                onClick={() => handleTagClick(tag)}
              >
                {tag}
              </Badge>
            ))}
            {activeTag && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => handleTagClick(activeTag)}
              >
                Clear filter
              </Button>
            )}
          </div>
        )}

        <Separator className="mb-6" />

        {/* Loading state */}
        {loading && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-40 bg-muted rounded-t-lg" />
                <CardContent className="pt-4 space-y-2">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty state / Coming Soon */}
        {!loading && filteredPosts.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Newspaper className="h-12 w-12 mx-auto mb-4 opacity-40" />
            {(searchQuery || activeTag) ? (
              <>
                <p className="text-lg">{t('blog.noArticles') || 'No articles found.'}</p>
                <Button
                  variant="link"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveTag('');
                    setSearchParams({});
                  }}
                >
                  {t('blog.clearFilters') || 'Clear filters'}
                </Button>
              </>
            ) : (
              <>
                <p className="text-xl font-semibold text-foreground mb-2">Coming Soon</p>
                <p className="text-sm max-w-md mx-auto">
                  We're working on strategy guides, deck spotlights, and more. Check back soon!
                </p>
              </>
            )}
          </div>
        )}

        {/* Post grid */}
        {!loading && filteredPosts.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post) => {
              const slug =
                typeof post.slug === 'string'
                  ? post.slug
                  : post.slug?.current;
              const coverUrl = post.coverImage
                ? urlFor(post.coverImage) || post.coverImage
                : null;

              return (
                <Card
                  key={post._id}
                  className="group overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
                  onClick={() => navigate(`/blog/${slug}`)}
                >
                  {/* Cover image or gradient placeholder */}
                  {coverUrl ? (
                    <img
                      src={coverUrl}
                      alt={post.title}
                      className="w-full h-40 object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                      <Newspaper className="h-10 w-10 text-primary/30" />
                    </div>
                  )}

                  <CardContent className="pt-4 space-y-2">
                    <h2 className="text-lg font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h2>

                    {post.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Meta row */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                      {post.author && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {post.author}
                        </span>
                      )}
                      {post.publishedAt && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(post.publishedAt)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {estimateReadTime(post)} min read
                      </span>
                    </div>

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {post.tags.slice(0, 3).map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="text-[10px] px-1.5 py-0"
                          >
                            {tag}
                          </Badge>
                        ))}
                        {post.tags.length > 3 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{post.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center text-xs text-primary font-medium pt-1 group-hover:gap-1.5 transition-all">
                      Read more <ChevronRight className="h-3 w-3" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
